import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { STALE_TIME_DAILY, STALE_TIME_SLOW } from './query';
import { apiClient } from './api';

export const PortionPresetSchema = z.object({ label: z.string(), grams: z.number() });
export type PortionPreset = z.infer<typeof PortionPresetSchema>;

export const DishSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  cal_per_100g: z.number(),
  protein_per_100g: z.number(),
  fat_per_100g: z.number(),
  carb_per_100g: z.number(),
  alcohol_per_100g: z.number().default(0),
  portion_presets: z.array(PortionPresetSchema),
  recipe_url: z.string().nullable(),
  evidence_raw: z.string().nullable(),
  llm_raw_json: z.unknown().nullable(),
  active: z.boolean(),
  rotation_status: z.enum(['private', 'candidate', 'active', 'archived', 'rejected']),
  source_type: z.enum(['cookidoo', 'description', 'manual']),
  meal_types: z.array(z.enum(['lunch', 'snack', 'dinner'])),
  cookidoo_recipe_id: z.string().nullable(),
  cookidoo_recipe_name: z.string().nullable(),
  recipe_image_url: z.string().nullable(),
  prep_time_minutes: z.number().nullable(),
  total_time_minutes: z.number().nullable(),
  servings_text: z.string().nullable(),
  difficulty: z.string().nullable(),
  ingredients_summary: z.string().nullable(),
  preparation_notes: z.string().nullable(),
  source_metadata_json: z.unknown().nullable(),
  content_hash: z.string().nullable(),
  candidate_origin: z.enum(['coach', 'operator']).nullable(),
  review_reason: z.string().nullable(),
  reviewed_at: z.number().nullable(),
  created_at: z.number(),
  updated_at: z.number(),
});
export type Dish = z.infer<typeof DishSchema>;

export const DishProposalSchema = z.object({
  name: z.string(),
  description: z.string(),
  cal_per_100g: z.number(),
  protein_per_100g: z.number(),
  fat_per_100g: z.number(),
  carb_per_100g: z.number(),
  alcohol_per_100g: z.number().default(0),
  portion_presets: z.array(PortionPresetSchema),
  recipe_url: z.string().nullable().optional(),
  confidence_note: z.string().optional(),
  evidence_raw: z.string(),
  llm_raw_json: z.unknown(),
  retries: z.number(),
  meal_types: z.array(z.enum(['lunch', 'snack', 'dinner'])).optional(),
  cookidoo_recipe_id: z.string().nullable().optional(),
  cookidoo_recipe_name: z.string().nullable().optional(),
  recipe_image_url: z.string().nullable().optional(),
  prep_time_minutes: z.number().nullable().optional(),
  total_time_minutes: z.number().nullable().optional(),
  servings_text: z.string().nullable().optional(),
  difficulty: z.string().nullable().optional(),
  ingredients_summary: z.string().nullable().optional(),
  preparation_notes: z.string().nullable().optional(),
  source_metadata_json: z.unknown().optional(),
});
export type DishProposal = z.infer<typeof DishProposalSchema>;

const MealItemSchema = z.object({
  id: z.number(), date: z.string(), slot: z.enum(['lunch', 'snack', 'dinner']),
  position: z.number(), dish_id: z.number(), dish_name: z.string(), grams: z.number(),
  cal: z.number(), p: z.number(), f: z.number(), c: z.number(), created_at: z.number(),
});
export type MealItem = z.infer<typeof MealItemSchema>;
const MealItemsResponseSchema = z.object({
  date: z.string(),
  slot: z.enum(['lunch', 'snack', 'dinner']),
  items: z.array(MealItemSchema),
  totals: z.object({ cal: z.number(), p: z.number(), f: z.number(), c: z.number() }),
});
export type MealItemsResponse = z.infer<typeof MealItemsResponseSchema>;

export function useDishes(includeInactiveOrOptions: boolean | {
  includeInactive?: boolean;
  mealType?: 'lunch' | 'snack' | 'dinner';
  query?: string;
} = false) {
  const options = typeof includeInactiveOrOptions === 'boolean'
    ? { includeInactive: includeInactiveOrOptions }
    : includeInactiveOrOptions;
  const params = new URLSearchParams();
  if (options.includeInactive) params.set('include_inactive', 'true');
  if (options.mealType) params.set('meal_type', options.mealType);
  if (options.query) params.set('q', options.query);
  const suffix = params.size ? `?${params}` : '';
  return useQuery({
    queryKey: ['dishes', options.includeInactive ? 'all' : 'active', options.mealType ?? 'all-slots', options.query ?? ''],
    queryFn: () => apiClient.get<Dish[]>(
      `/api/dishes${suffix}`,
      { schema: z.array(DishSchema) },
    ),
    staleTime: STALE_TIME_SLOW,
  });
}

export function characterizeDish(body: { name?: string; evidence: string; evidence_type: 'cookidoo' | 'description' }) {
  return apiClient.post<DishProposal>('/api/dishes/characterize', body, {
    schema: DishProposalSchema,
    stashOnNetworkError: false,
  });
}

export type DishWrite = Omit<Dish, 'id' | 'active' | 'created_at' | 'updated_at'>;

export function useMealItems(date: string, slot: 'lunch' | 'snack' | 'dinner') {
  return useQuery({
    queryKey: ['meal-items', date, slot],
    queryFn: () => apiClient.get<MealItemsResponse>(
      `/api/daily/${date}/meals/${slot}/items`,
      { schema: MealItemsResponseSchema },
    ),
    staleTime: STALE_TIME_DAILY,
  });
}

export function useLogMealItems(date: string, slot: 'lunch' | 'snack' | 'dinner') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: Array<{
      dish_id: number;
      grams?: number;
      preset_label?: string;
      macros?: { cal: number; p: number; f: number; c: number };
    }>) =>
      apiClient.put<MealItemsResponse>(`/api/daily/${date}/meals/${slot}/items`, { items }, {
        schema: MealItemsResponseSchema,
      }),
    onSuccess: (value) => qc.setQueryData(['meal-items', date, slot], value),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['daily', date] });
      qc.invalidateQueries({ queryKey: ['daily'] });
      qc.invalidateQueries({ queryKey: ['meal-items', date, slot] });
    },
  });
}

const CharacterizedMealResponseSchema = MealItemsResponseSchema.extend({
  dish_id: z.number(),
  rotation_status: z.enum(['private', 'candidate']),
});

export function useLogCharacterizedMeal(date: string, slot: 'lunch' | 'snack' | 'dinner') {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['log-characterized-meal', date, slot],
    mutationFn: (body: {
      dish: Omit<DishProposal, 'confidence_note' | 'retries'>;
      grams?: number;
      preset_label?: string;
      suggest_for_rotation: boolean;
      append_to_meal?: boolean;
    }) => apiClient.post(`/api/daily/${date}/meals/${slot}/characterized`, body, {
      schema: CharacterizedMealResponseSchema,
      stashOnNetworkError: false,
    }),
    onSuccess: (value) => qc.setQueryData(['meal-items', date, slot], value),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['daily', date] });
      qc.invalidateQueries({ queryKey: ['daily'] });
      qc.invalidateQueries({ queryKey: ['meal-items', date, slot] });
      // Refresh inactive catalog queries too: the rotation logger is unmounted
      // while the operator is on the New item tab.
      qc.invalidateQueries({ queryKey: ['dishes'], refetchType: 'all' });
    },
  });
}
